import {setMeshToLayer} from "./LayerHandler";

import { threeEngine } from 'Domain/Scene/Engine/ThreeEngine';
import { animationStore } from 'Domain/Mesh/State/AnimationStore';

// Export archMatrix for backward compatibility
export const archMatrix = animationStore.getArchMatrix();

/*****************************************************************
 * GENGIVAS ANIMATION
 * ***************************************************************/

const showGengiva = (position, keyframe) => {
    const gengivas = animationStore.getGengivas();
    const maxGengivaKey = animationStore.getMaxGengivaKey(position);
    
    Object.entries(gengivas[position]).forEach( 
        ([key, mesh]) => {
            if (mesh.userData?.manualVisibility) return;
            mesh.visible = (gengivas[position][keyframe] !== undefined) ?
                (parseInt(key) === parseInt(keyframe)) : 
                (parseInt(key) === parseInt(maxGengivaKey));
        }
    );
};

/**
 * Assign Gengiva in matrix
 * @param {object} gengiva 
 * @param {string} position 
 * @param {number} key 
 */
export const addGengiva = (gengiva, position, key) => {
    animationStore.addGengiva(gengiva, position, key);
}

/**
 * Assign gengivas visibility property
 * @param {number} keyframe 
 */
export const showGengivas = keyframe => 
    Object.keys(animationStore.getGengivas()).forEach(position => 
        showGengiva(position, keyframe));

/*****************************************************************
 * PONTIC ANIMATION
 * ***************************************************************/

const showPontic = (in_sPosition, in_nKeyframe) => {
    const oPontics = animationStore.getPontics();
    const oPositionPontics = oPontics[in_sPosition];
    if (!oPositionPontics) return;

    const aKeys = Object.keys(oPositionPontics)
        .map(sKey => parseInt(sKey))
        .filter(nKey => Number.isFinite(nKey))
        .sort((nFirst, nSecond) => nFirst - nSecond);

    if (aKeys.length === 0) return;

    const nCurrentKeyframe = parseInt(in_nKeyframe);
    let nTargetKeyframe = aKeys[0];

    for (const nKey of aKeys) {
        if (nKey > nCurrentKeyframe) break;
        nTargetKeyframe = nKey;
    }
    
    Object.entries(oPositionPontics).forEach(
        ([sKey, oMesh]) => {
            if (oMesh.userData?.manualVisibility) return;
            oMesh.visible = parseInt(sKey) === nTargetKeyframe;
        }
    );
};

/**
 * Assign Pontic in matrix
 * @param {object} pontic 
 * @param {string} position 
 * @param {number} key 
 */
export const addPontic = (pontic, position, key) => {
    animationStore.addPontic(pontic, position, key);
}

/**
 * Assign pontics visibility property
 * @param {number} keyframe 
 */
export const showPontics = keyframe => 
    Object.keys(animationStore.getPontics()).forEach(position => 
        showPontic(position, keyframe));

/*****************************************************************
 * COMPARATIVE GENCIVAS AND PONTICS
 * ***************************************************************/

export const fillGengivaComparative = (scene) => {
    let comparativeGroup = threeEngine.getComparativeSceneGroup();
    const gengivas = animationStore.getGengivas();
    const gengivasComparative = animationStore.getGengivasComparative();
    const gengivasMaloc = animationStore.getGengivasMaloc();
    const gengivasSetup = animationStore.getGengivasSetup();

    ["maxi", "mandi"].forEach(position => {
        if (gengivas[position] && Object.keys(gengivas[position]).length > 0) {
            const maxGengivaKey = animationStore.getMaxGengivaKey(position);
            
            gengivasComparative[position][0] = gengivas[position][0].clone();
            gengivasComparative[position][1] = gengivas[position][maxGengivaKey].clone();
            gengivasMaloc[position][0] = gengivas[position][0].clone();
            gengivasSetup[position][1] = gengivas[position][maxGengivaKey].clone();

            setMeshToLayer(gengivasComparative[position][0], `comparative${position.charAt(0).toUpperCase() + position.slice(1)}`);
            setMeshToLayer(gengivasComparative[position][1], `comparative${position.charAt(0).toUpperCase() + position.slice(1)}`);
            setMeshToLayer(gengivasMaloc[position][0], `maloc${position.charAt(0).toUpperCase() + position.slice(1)}`);
            setMeshToLayer(gengivasSetup[position][1], `setup${position.charAt(0).toUpperCase() + position.slice(1)}`);

            comparativeGroup.add(gengivasMaloc[position][0]);
            comparativeGroup.add(gengivasSetup[position][1]);
            comparativeGroup.add(gengivasComparative[position][0]);
            comparativeGroup.add(gengivasComparative[position][1]);
            if (position === "mandi") { //Pour le ByteJump
                threeEngine.addToAllMandiStuff(gengivasMaloc[position][0]);
                threeEngine.addToAllMandiStuff(gengivasSetup[position][1]);
                threeEngine.addToAllMandiStuff(gengivasComparative[position][0]);
                threeEngine.addToAllMandiStuff(gengivasComparative[position][1]);
            }

            gengivasMaloc[position][0].visible = true;
            gengivasSetup[position][1].visible = true;
        } else {
            console.warn(`gengivas[${position}] est vide ou non défini`);
        }
    });
};


export const showComparativeElement = (inSetup) => {
    let [elementToShow, elementToHide] = inSetup ? [1, 0] : [0, 1];
    const gengivasComparative = animationStore.getGengivasComparative();

    ["maxi", "mandi"].forEach(position => {
        if (
            gengivasComparative[position] &&
            gengivasComparative[position][elementToShow] &&
            gengivasComparative[position][elementToHide]
        ) {
            gengivasComparative[position][elementToShow].visible = true;
            gengivasComparative[position][elementToHide].visible = false;
        } else {
            console.warn(`gengivasComparative[${position}] n'est pas défini correctement.`);
        }
    });
};

/*****************************************************************
 * TRACK ANIMATION
 * ***************************************************************/

/**
 * Animation step index
 * @param {object} animation
 */
export const assignTimelineStep = animation => new Promise( (resolve, reject) => {
    animation.tracks.forEach(track =>
      resolve(track.times.forEach(time => {
        animationStore.addTime(time);
      }))
    )
  })

// Export times for backward compatibility - utilise une fonction getter pour éviter l'export direct
export const getTimes = () => animationStore.getTimes();

// Export pontics for backward compatibility - utilise une fonction getter pour éviter l'export direct
export const getPonticsData = () => animationStore.getPontics();

/**
 * Assign visibility property for Attachment and Stripping
 * @param {object} userData 
 * @param {number} keyframe 
 * @returns boolean
 */
export const isVisible = (userData, keyframe) => {
    return parseInt(userData.startStep) <= parseInt(keyframe) &&
        parseInt(keyframe) <= parseInt(userData.endStep);
}
